// The `project` (repo) wire shape as `/v1/repos` serves it — the list, the
// detail read, and the enable/donations toggles that answer the updated row.
//
// ## This is NOT the MCP `project` body, and merging them would lose fields
//
// `packages/mcp/src/army/project.ts`'s `maskProject` answers about the same
// row and shares only six keys with this one. Measured on main:
//
// | Only on REST | Only on MCP |
// |---|---|
// | `donations_enabled` | `private` |
// | `coderabbit_warnings` | `enable_refusal_reason` (present only when billing is why a row is off) |
// | `policy_id`, `policy_hash`, `policy_valid` | `github_repo_id` |
// | `last_activity_iso` | |
// | `canary_enabled`, `public_stats_enabled` (MCP serves both on `project get` and the switch echoes, not in `maskProject`) | |
//
// And the creation stamp is spelled `created_at` here and `created_iso` there.
// Neither body is a subset of the other, so a single resource would have to add
// six fields to one door or drop them from it — and `/v1` is published. The
// spelling is the sharper half: renaming `created_at` breaks every dashboard
// read of this collection, and renaming `created_iso` breaks every agent that
// has parsed a `project list`. Both stay, and the divergence is written down
// here rather than rediscovered.
//
// ## The four derived fields are INPUTS
//
// `coderabbit_warnings`, `policy_valid` and `last_activity_iso` are batched
// lookups the route performs for a whole page, not columns on the row. They
// ride the entity so the projection stays pure and so the list and the detail
// read cannot compute them differently — which is exactly what a second
// single-repo projection would have allowed.

import { z } from 'zod';
import { type AiFirstScore, AiFirstScoreSchema } from '../entities/ai-first-score';
import { defineResource } from './resource';

/** The camelCase row plus the three batched lookups the route resolves for it. */
export type ProjectEntity = {
  repo: {
    id: string;
    fullName: string;
    defaultBranch: string;
    enabled: boolean;
    donationsEnabled: boolean;
    canaryEnabled: boolean;
    publicStatsEnabled: boolean;
    aiFirstAt: Date | null;
    /**
     * The stored scorecard (`repositories.ai_first_score`, latest run wins), read
     * per repo by the single-repo projection only — absent on a list row, see
     * {@link ProjectWireSchema}'s `ai_first_score`.
     */
    aiFirstScore?: AiFirstScore | null;
    policyId: string | null;
    policyHash: string | null;
    createdAt: Date;
  };
  /** Open CodeRabbit warnings on this repo, batched per page. `0` when none. */
  coderabbitWarnings: number;
  /** `null` = no active policy snapshot — the dashboard's 'missing', not 'invalid'. */
  policyValid: boolean | null;
  lastActivity: Date | null;
};

/** The published body. Field names and nullability are the existing contract. */
export const ProjectWireSchema = z.object({
  id: z.string(),
  full_name: z.string(),
  default_branch: z.string(),
  enabled: z.boolean(),
  /**
   * The owner's "accept donations" opt-in — the toggle's own state, so the
   * dashboard renders what is stored rather than what it last sent. NOT "is
   * donatable": that is `enabled AND NOT private AND donations_enabled`,
   * evaluated in the donatable query and never re-derived per surface.
   */
  donations_enabled: z.boolean(),
  /**
   * The owner's daily-canary opt-in (`project set_canary`, `PATCH … canary_enabled`)
   * as STORED — the toggle's own state. Whether a canary is actually filed also
   * needs `enabled`; the series and its grade are `status {repo}`.
   */
  canary_enabled: z.boolean(),
  /**
   * The owner's consent to publish this repo's agent-authored health numbers on
   * the public stats route (#5660) as STORED — the toggle's own state. Whether
   * the route serves them also needs `enabled` and `not private`
   * (`findPublicRepoGate`).
   */
  public_stats_enabled: z.boolean(),
  /**
   * When repo onboarding LOOKED at this repo. `null` = never onboarded, and
   * coding tasks on it are HELD — so this is what a maintainer reads to explain
   * a queue that is not moving.
   */
  ai_first_at: z.string().nullable(),
  /**
   * The AI-first SCORECARD the latest onboarding run stored (epic #3756 door 2):
   * one item per checklist entry, each `ok | gap | refused | unprovable` with its
   * evidence. `null` = no pass has scored this repo. Carried by every SINGLE-repo
   * answer (the detail read, the toggles, create) and absent from the list: it is
   * a per-repo read with no batched form, and a page of scorecards is a view
   * nobody renders.
   */
  ai_first_score: AiFirstScoreSchema.nullable().optional(),
  policy_id: z.string().nullable(),
  policy_hash: z.string().nullable(),
  /** `null` = no active snapshot. Three states, not a boolean with a default. */
  policy_valid: z.boolean().nullable(),
  last_activity_iso: z.string().nullable(),
  created_at: z.string(),
  coderabbit_warnings: z.number(),
});

/** What a client should type against for a project body. */
export type ProjectWire = z.infer<typeof ProjectWireSchema>;

export const ProjectResource = defineResource<ProjectEntity, typeof ProjectWireSchema>({
  schema: ProjectWireSchema,
  serialize: ({ repo: r, coderabbitWarnings, policyValid, lastActivity }) => ({
    id: r.id,
    full_name: r.fullName,
    default_branch: r.defaultBranch,
    enabled: r.enabled,
    donations_enabled: r.donationsEnabled,
    canary_enabled: r.canaryEnabled,
    public_stats_enabled: r.publicStatsEnabled,
    ai_first_at: r.aiFirstAt ? r.aiFirstAt.toISOString() : null,
    ...(r.aiFirstScore !== undefined ? { ai_first_score: r.aiFirstScore } : {}),
    coderabbit_warnings: coderabbitWarnings,
    policy_id: r.policyId,
    policy_hash: r.policyHash,
    policy_valid: policyValid,
    last_activity_iso: lastActivity ? lastActivity.toISOString() : null,
    created_at: r.createdAt.toISOString(),
  }),
});
